import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ArrowRight, Loader2, Newspaper } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
};

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-8 py-16" data-testid="blog-index">
      <Seo
        title="Articles & Resources | Centennial Business Systems"
        description="Practical operating articles for running dealership service, parts, sales, HR and collision departments — built on Process → Procedure → Reference."
        canonicalPath="/blog"
      />
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold">
          <Newspaper className="w-4 h-4" /> Insights
        </span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-3 leading-[1.05]">
          Running a better dealership, one process at a time.
        </h1>
        <p className="text-muted-foreground text-lg mt-4">
          Practical guides on service, HR, safety, parts, and body shop operations — from the team behind
          the Centennial Dealership Operating System.
        </p>
      </div>

      {posts === null ? (
        <div className="flex items-center gap-2 text-muted-foreground mt-16"><Loader2 className="w-5 h-5 animate-spin" /> Loading articles…</div>
      ) : posts.length === 0 ? (
        <div className="mt-16 border border-dashed border-border p-10 text-center" data-testid="blog-empty">
          <Newspaper className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground mt-3">New articles are on the way. Check back soon.</p>
          <Link to="/forms" className="inline-flex items-center gap-2 text-primary font-semibold mt-4">
            Browse the form library <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {posts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
              className="group bg-white border border-border p-7 flex flex-col hover:border-primary transition-colors">
              {p.category && <span className="text-[11px] uppercase tracking-wider font-semibold text-primary">{p.category}</span>}
              <h2 className="font-heading font-bold text-xl mt-2 leading-snug group-hover:text-primary transition-colors">{p.title}</h2>
              <p className="text-sm text-muted-foreground mt-3 flex-1">{p.excerpt}</p>
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
                <span className="text-xs text-muted-foreground">{fmtDate(p.date)}</span>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">
                  Read <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
